import { useRedeemLink } from '@myrentalmate/api-client';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { useState } from 'react';

import { okData } from '../../lib/apiData';
import { brand } from '../../lib/brand';
import { useFragmentToken } from '../../lib/useFragmentToken';
import { TwoFactorChallenge } from './TwoFactorChallenge';

/**
 * /signin#<token>. The token is in the fragment, so it never reaches server logs. A click is
 * required because email security scanners open links automatically and would use it up.
 */
export function RedeemLinkPage() {
  const token = useFragmentToken();
  const [challenge, setChallenge] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const redeem = useRedeemLink({
    mutation: {
      onSuccess: async (response) => {
        const answer = okData(response);
        if (answer?.mfa_required) {
          setChallenge(answer.challenge);
          return;
        }
        await queryClient.invalidateQueries();
        navigate('/', { replace: true });
      },
    },
  });

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 420, width: '100%', textAlign: 'center' }}>
        <Typography variant="h4" gutterBottom>
          {brand.name}
        </Typography>
        {challenge ? (
          <TwoFactorChallenge
            challenge={challenge}
            onSignedIn={() => navigate('/', { replace: true })}
          />
        ) : !token || redeem.isError ? (
          <Typography color="text.secondary">
            This link isn't valid. It may have expired or already been used. Contact{' '}
            {brand.supportEmail} for a new one.
          </Typography>
        ) : (
          <Button
            variant="contained"
            size="large"
            disabled={redeem.isPending}
            onClick={() => redeem.mutate({ data: { token } })}
          >
            {redeem.isPending ? 'Signing in…' : 'Confirm sign-in'}
          </Button>
        )}
      </Paper>
    </Box>
  );
}
